import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { appLocale } from '../i18n/locale';

/**
 * Booking -> construction -> completion, with the DLD project number that ties
 * the card to the Mashrooi fact sheet.
 *
 * Extracted from PropertyView's own detail screen so the owner shell can show
 * it on the Property Management detail too. When the shell moved the unit
 * screen over to that detail, this card was the one thing left behind — an
 * off-plan owner opening their unit lost the only view of where the building
 * actually is. One copy, both screens.
 *
 * Reads nothing but the unit: constructionCompletionPercent,
 * constructionRegistrationDate / StartDate / CompletionDate,
 * completionDateResolved and its source, plus estimatedCompletionDate. DLD
 * ships 0001-01-01 as its "no date" placeholder, which formatDate drops rather
 * than printing a first-century date.
 */
const ProjectTimeline: React.FC<{ unit: any }> = ({ unit: selectedUnit }) => {
  const { t } = useLanguage();

  const shows = !!(selectedUnit && (
    selectedUnit.constructionCompletionPercent != null ||
    selectedUnit.constructionRegistrationDate ||
    selectedUnit.constructionStartDate ||
    selectedUnit.constructionCompletionDate ||
    selectedUnit.occupancyStatus === 'Offplan' ||
    (selectedUnit.estimatedCompletionDate && selectedUnit.estimatedCompletionDate !== 'null' && selectedUnit.estimatedCompletionDate !== '')
  ));
  if (!shows) return null;

      const pct = selectedUnit.constructionCompletionPercent ?? 0;
      // The backend resolves this: DLD's date when it publishes a real one, else
      // the date read off the SPA/OTP. Older payloads have neither field, so keep
      // the previous chain as a fallback — DLD's 0001-01-01 placeholder is truthy,
      // so it has to be filtered here or it beats the SPA date to the slot.
      const legacyCompletion = selectedUnit.constructionCompletionDate &&
        new Date(selectedUnit.constructionCompletionDate).getFullYear() >= 2000
          ? selectedUnit.constructionCompletionDate
          : selectedUnit.estimatedCompletionDate;
      const completionDateStr = selectedUnit.completionDateResolved || legacyCompletion;
      const completionSource = selectedUnit.completionDateSource;
      const isFullyComplete = pct >= 100;

      // Determine which stages are done
      const bookingDone = !!(selectedUnit.constructionRegistrationDate || pct > 0 || selectedUnit.constructionStartDate);
      const constructionDone = isFullyComplete;
      const handoverDone = isFullyComplete && completionDateStr && new Date(completionDateStr) <= new Date();

      const tealColor = '#6b9e9e';
      const grayLine = '#d1d5db';

      const formatDate = (d: string | null | undefined) => {
        if (!d) return null;
        const date = new Date(d);
        // Filter out placeholder dates from DLD (0001-01-01)
        if (date.getFullYear() < 2000) return null;
        return date.toLocaleDateString(appLocale(), { month: 'long', day: 'numeric', year: 'numeric' });
      };

      return (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '1rem',
          padding: '1.5rem',
          border: '1px solid #f3f4f6',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Section Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.25rem' }}>&#128197;</span>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', margin: 0 }}>
              {t('projectTimeline') || 'Project Timeline'}
            </h2>
            {/* DLD project number — the key that ties this card to the Mashrooi fact sheet */}
            {selectedUnit.constructionProjectNumber && (
              <span style={{
                marginInlineStart: 'auto', padding: '2px 10px', borderRadius: '999px',
                backgroundColor: '#f3f4f6', color: '#4b5563',
                fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap'
              }}>
                {t('pvDldProjectNo') || 'DLD Project'} #{selectedUnit.constructionProjectNumber}
              </span>
            )}
          </div>

          <div style={{ position: 'relative', paddingLeft: '40px' }}>
            {/* Vertical connecting line */}
            <div style={{
              position: 'absolute',
              left: '15px',
              top: '16px',
              bottom: '16px',
              width: '3px',
              background: `linear-gradient(to bottom, ${bookingDone ? tealColor : grayLine} 0%, ${bookingDone ? tealColor : grayLine} 33%, ${constructionDone ? tealColor : grayLine} 33%, ${constructionDone ? tealColor : grayLine} 66%, ${handoverDone ? tealColor : grayLine} 66%, ${handoverDone ? tealColor : grayLine} 100%)`
            }} />

            {/* Stage 1: Booking Started */}
            <div style={{ position: 'relative', marginBottom: '28px' }}>
              {/* Circle */}
              <div style={{
                position: 'absolute', left: '-40px', top: '0',
                width: '32px', height: '32px', borderRadius: '50%',
                backgroundColor: bookingDone ? tealColor : '#ffffff',
                border: bookingDone ? 'none' : `3px solid ${grayLine}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1
              }}>
                {bookingDone && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div style={{ fontWeight: '700', color: '#374151', fontSize: '1rem' }}>
                {t('bookingStarted') || 'Booking Started'}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9375rem', marginTop: '2px' }}>
                {formatDate(selectedUnit.constructionRegistrationDate) || '—'}
              </div>
            </div>

            {/* Stage 2: Construction Started */}
            <div style={{ position: 'relative', marginBottom: '28px' }}>
              {/* Circle */}
              <div style={{
                position: 'absolute', left: '-40px', top: '0',
                width: '32px', height: '32px', borderRadius: '50%',
                backgroundColor: (pct > 0 || selectedUnit.constructionStartDate) ? tealColor : '#ffffff',
                border: (pct > 0 || selectedUnit.constructionStartDate) ? 'none' : `3px solid ${grayLine}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1
              }}>
                {(pct > 0 || selectedUnit.constructionStartDate) && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div style={{ fontWeight: '700', color: '#374151', fontSize: '1rem' }}>
                {t('constructionStarted') || 'Construction Started'}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9375rem', marginTop: '2px' }}>
                {formatDate(selectedUnit.constructionStartDate) || '—'}
              </div>

              {/* Construction progress bar */}
              {pct > 0 && pct < 100 && (
                <div style={{ marginTop: '14px' }}>
                  <div style={{ fontSize: '0.9375rem', color: '#374151', fontWeight: '500', marginBottom: '8px' }}>
                    {t('pvConstructionProgress')}: {pct.toFixed(pct % 1 === 0 ? 0 : 1)}%
                  </div>
                  <div style={{
                    width: '100%', height: '12px', backgroundColor: '#e5e7eb',
                    borderRadius: '6px', overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '6px',
                      backgroundColor: tealColor,
                      width: `${Math.min(100, Math.max(0, pct))}%`,
                      transition: 'width 0.7s ease-out'
                    }} />
                  </div>
                  {selectedUnit.constructionLastSync && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                      <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>
                        {t('pvLatestUpdate')}: <span style={{ fontWeight: '600', color: '#6b7280' }}>
                          {new Date(selectedUnit.constructionLastSync).toLocaleDateString(appLocale(), { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
              {pct >= 100 && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle style={{ width: '16px', height: '16px', color: tealColor }} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    {t('pvConstructionCompleted')}
                  </span>
                </div>
              )}
            </div>

            {/* Stage 3: Expected Completion */}
            <div style={{ position: 'relative' }}>
              {/* Circle */}
              <div style={{
                position: 'absolute', left: '-40px', top: '0',
                width: '32px', height: '32px', borderRadius: '50%',
                backgroundColor: handoverDone ? tealColor : '#ffffff',
                border: handoverDone ? 'none' : `3px solid ${grayLine}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1
              }}>
                {handoverDone && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div style={{ fontWeight: '700', color: '#374151', fontSize: '1rem' }}>
                {handoverDone ? (t('completed') || 'Completed') : (t('expectedCompletion') || 'Expected Completion')}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9375rem', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span>{formatDate(completionDateStr) || '—'}</span>
                {/* DLD leaves this blank for plenty of projects (it prints 0001-01-01).
                    When the date came off the buyer's own SPA/OTP instead, say so —
                    an unlabelled date reads as registry-confirmed when it isn't. */}
                {completionDateStr && completionSource && completionSource !== 'DLD' && (
                  <span style={{
                    padding: '1px 8px', borderRadius: '999px',
                    backgroundColor: '#fef3c7', color: '#92400e',
                    fontSize: '0.6875rem', fontWeight: 600, whiteSpace: 'nowrap'
                  }}>
                    {t('pvPerDocument') || 'per'} {completionSource}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
};

export default ProjectTimeline;
